import {CookieManager} from '../modules/cookie.js';
import {getPrefixedEvent} from '../utils/animation.js';
import {listenOnce} from '../utils/listenOnce.js';
import {getHttpClient} from '../modules/httpClient.js';
import {Button} from '../modules/button.js';
import {Notification} from '../modules/notification.js';

const ANIMATION_END = getPrefixedEvent('animationend');
const ANIMATE_IN = 'floatingHelpToolbar--animate-in';
const ANIMATE_OUT = 'floatingHelpToolbar--animate-out';
const ANIMATE_INITIAL = 'floatingHelpToolbar--animate-initial';
const INTERACTABLE = 'floatingHelpToolbar--interactable';

const IS_OPEN = 'is-open';
const IS_CLOSE = 'is-closed';

let helpToolbar;

/**
 * Register trigger events to display and hide help toolbar's body
 */
function registerTriggerEvents() {
    const trigger = helpToolbar.querySelector('.js-floatingHelpToolbar__trigger');

    const hideHelper = function () {
        const isNotOpened = !helpToolbar.classList.contains(IS_OPEN);
        if (isNotOpened) return;
        setTimeout(() => {
            helpToolbar.classList.remove(IS_OPEN);
            helpToolbar.classList.add(IS_CLOSE);
        }, 500);
    };

    trigger.addEventListener('click', function (clickEvent) {
        clickEvent.preventDefault();
        helpToolbar.classList.remove(ANIMATE_IN);
        helpToolbar.classList.remove(IS_CLOSE);
        helpToolbar.classList.add(IS_OPEN);
    });

    // previously using 'focusout' event but it's seems not working with
    // button element
    // @see: http://developer.mozilla.org/en-US/docs/Web/HTML/Element/Button#Clicking_and_focus
    helpToolbar.addEventListener('mouseleave', () => hideHelper());
}

/**
 * @returns {boolean}
 */
function wasToolbarDisplayedBefore() {
    return CookieManager.read('was_help_toolbar_displayed');
}

/** */
function saveHintDisplayed() {
    if (wasToolbarDisplayedBefore()) return;
    CookieManager.write('was_help_toolbar_displayed', 'true', {
        days: 1,
    });
}

/**
 * @returns {Promise}
 */
function collapseHint() {
    const promise = listenOnce(helpToolbar, ANIMATION_END);
    helpToolbar.classList.remove(ANIMATE_IN);
    helpToolbar.classList.add(ANIMATE_OUT);
    return promise;
}

/** */
function enabledTriggerInteractivity() {
    helpToolbar.classList.remove(ANIMATE_IN);
    helpToolbar.classList.remove(ANIMATE_OUT);
    helpToolbar.classList.add(INTERACTABLE);
}

/**
 * @returns {Promise}
 */
function displayHint() {
    const DISPLAY_DURATION = 3000;
    return new Promise((resolve) => setTimeout(resolve, DISPLAY_DURATION));
}

/**
 * @returns {Promise}
 */
function expandHint() {
    const promise = listenOnce(helpToolbar, ANIMATION_END);
    helpToolbar.classList.remove(ANIMATE_INITIAL);
    helpToolbar.classList.add(ANIMATE_IN);
    return promise;
}

/**
 * @returns {Promise}
 */
function breakIfTriggerHasBeingClicked() {
    return new Promise((resolve, reject) => {
        const hasInteractedDuringTransition =
            helpToolbar.classList.contains(IS_OPEN) || helpToolbar.classList.contains(IS_CLOSE);

        if (hasInteractedDuringTransition) reject(new Error());
        resolve();
    });
}

/** [no description; please add] */
function initReportErrorDialog() {
    const reportErrorDialog = document.querySelector('#reportErrorDialog');
    reportErrorDialog?.addEventListener('submit', async (event) => {
        event.preventDefault();

        const form = event.target;
        const formData = new FormData(form);
        const submitButton = reportErrorDialog.querySelector('[type="submit"]');

        Button.addSpinner(submitButton);
        try {
            const response = await getHttpClient().post(form.action, formData);
            if (response) {
                reportErrorDialog.close();
                // Display a success message
                Notification.success({
                    title: 'Thanks for contacting us!',
                    message: response.message,
                });
            }
        } catch (error) {
            Notification.error(error.message);
        } finally {
            Button.removeSpinner(submitButton);
        }
    });
}

/**
 * Initialize component, register all event handlers
 */
function initializeComponent() {
    helpToolbar = document.querySelector('.js-floatingHelpToolbar');

    registerTriggerEvents();
    initReportErrorDialog();

    const hasInitialHintAnimation = helpToolbar.classList.contains(ANIMATE_INITIAL);
    const isInLessonPage = document.querySelector('.js-lessonItemWrapper') !== null;

    if (hasInitialHintAnimation) {
        expandHint()
            .then(() => displayHint())
            .then(() => {
                if (isInLessonPage) saveHintDisplayed();
                return breakIfTriggerHasBeingClicked();
            })
            .then(() => collapseHint())
            .then(() => {
                enabledTriggerInteractivity();
            })
            .catch(() => {
                // It's okay. That's mean user has clicked
                // on help toolbar and right now is showing
                // its contents.
                enabledTriggerInteractivity();
                if (isInLessonPage) saveHintDisplayed();
            });
        return;
    }
}

document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initializeComponent)
    : initializeComponent();
